import { useState, useRef, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import DyanamicDropdown from "./DyamicDropdown";
import { handleSort, formatAssetDisplayName } from "helpers/utils";

// Minimum width to ensure dropdown is wide enough for content
const MIN_DROPDOWN_WIDTH = 600;

const useStyles = makeStyles()((theme) => ({
  dropdownContainer: {
    position: "relative",
    width: "100%"
  }
}));

const ModelAssetDropdown = ({
  assets,
  selectedAsset,
  onChange,
  onClear,
  count,
  isReadOnly,
  disabled,
  customCaptions,
  showHeader = true,
  hasArrangements = false,
  showAssetReferences,
  required = false,
  isError = false,
  label,
  placeholder,
  onPageChange,
  page,
  handleServerSideSearch,
  fetchData,
  isServerSide,
}) => {
  const { classes } = useStyles();
  const [inputWidth, setInputWidth] = useState(MIN_DROPDOWN_WIDTH);
  const dropdownRef = useRef(null);

  // Measure the width of the input element
  useEffect(() => {
    if (dropdownRef.current) {
      const inputElement = dropdownRef.current.querySelector('.inputbox');
      if (inputElement) {
        // Use ResizeObserver to track changes in width
        const resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            // Always use the larger of the minimum width or input width
            setInputWidth(Math.max(entry.contentRect.width, MIN_DROPDOWN_WIDTH));
          }
        });
        
        resizeObserver.observe(inputElement);
        
        // Initial measurement
        setInputWidth(Math.max(inputElement.offsetWidth, MIN_DROPDOWN_WIDTH));
        
        return () => {
          resizeObserver.disconnect();
        };
      }
    }
  }, []);

  // Set column width based on showAssetReferences and hasArrangements
  const columnWidth = hasArrangements 
    ? showAssetReferences ? "25%" : "33%" 
    : showAssetReferences ? "33%" : "50%";
  
  // Base header and columns
  const dataHeader = [
    { id: 1, name: customCaptions?.asset ?? "Asset", width: columnWidth },
  ];
  
  const columns = [
    { id: 1, name: "name" },
  ];
  
  // Add Asset Reference column if needed
  if (showAssetReferences) {
    dataHeader.push({
      id: 2, 
      name: customCaptions?.assetReference ?? "Asset Reference",
      width: columnWidth,
    });
    
    columns.push({
      id: 2,
      name: "assetReferenceName",
    });
  }
  
  // Add Description column
  dataHeader.push({
    id: 3,
    name: "Description",
    width: hasArrangements ? (showAssetReferences ? "25%" : "33%") : (showAssetReferences ? "34%" : "50%"),
  });
  
  columns.push({
    id: 3,
    name: showAssetReferences ? "assetReferenceDescription" : "description",
  });
  
  // Add Arrangement column if needed
  if (hasArrangements) {
    dataHeader.push({
      id: 4,
      name: customCaptions?.arrangement ?? "Arrangement",
      width: showAssetReferences ? "25%" : "34%",
    });
    
    columns.push({
      id: 4,
      name: "arrangementName",     
    });
  }

  // Format selected value to show arrangement if available
  const formatSelectedValue = () => {
    if (selectedAsset?.name) {
      return {
        ...selectedAsset,
        name: formatAssetDisplayName(selectedAsset) 
      };
    }
    return selectedAsset;
  };

  return (
    <div className={classes.dropdownContainer} ref={dropdownRef}>
      <DyanamicDropdown
        width="100%"
        dropDownActiveWidth={`${inputWidth}px`}
        dataSource={assets}
        dataHeader={dataHeader}
        columns={columns}
        showHeader={showHeader}
        onChange={onChange}
        selectedValue={formatSelectedValue()}
        selectdValueToshow="name"
        count={count}
        handleSort={handleSort}
        isReadOnly={isReadOnly}
        disabled={disabled}
        showClear
        onClear={onClear}
        placeholder={placeholder || `Select ${customCaptions?.asset}`}
        label={label ?? customCaptions?.asset}
        required={required}
        isError={isError}
        onPageChange={onPageChange}
        page={page}
        handleServerSideSearch={handleServerSideSearch}
        isServerSide={isServerSide}
        fetchData={fetchData}
      />
    </div>
  );
};

export default ModelAssetDropdown; 